import { Alert, AlertIcon, Box, Button, Center, Divider, Flex, Heading, HStack, Icon, Link, ListItem, Spinner, Stack, Text, UnorderedList, useToast } from "@chakra-ui/react";
import { Tooltip } from "../../../common/components/Tooltip";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FiArrowLeft, FiCheckCircle, FiDownload } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import {
  useFinaliseProtocolMutation,
  useGetProtocolDraftQuery,
  useGetSourcingEventQuery,
  useListProtocolVersionsQuery,
  usePatchProtocolNarrativeMutation,
  type NarrativeBasisDto,
  type ProtocolDocumentDto,
} from "../../../autogen/bff-api";
import { useCreateEditor, useEditor } from "../../../common/editing/save-coordinator";
import { useApiError } from "../../../common/errors/useApiError";
import { displayDateWithTime } from "../../../common/formatting/displayDateWithTime";
import { momentLocaleFor } from "../../../common/formatting/i18nFormatters";
import { ListLoadError } from "../../../common/ListLoadError";
import { useConfirmationModal } from "../../../common/useConfirmationModal";
import { downloadProtocolVersion } from "./downloadProtocolVersion";
import { ProtocolSectionView } from "./ProtocolSectionView";
import { useRecordTitle } from "../../../common/page-title/PageTitle";

export const ProtocolPage = () => {
  const { t, i18n } = useTranslation();
  const { eventId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const apiError = useApiError();
  const locale = momentLocaleFor(i18n.language);
  const confirmationModal = useConfirmationModal();

  const {
    data: draft,
    isLoading,
    isError: isDraftError,
    isFetching: isFetchingDraft,
    refetch: refetchDraft,
  } = useGetProtocolDraftQuery(eventId ? { eventId } : skipToken);
  const [patch] = usePatchProtocolNarrativeMutation();
  const [finalise, { isLoading: isFinalising }] = useFinaliseProtocolMutation();
  const { data: versionsData, refetch: refetchVersions } = useListProtocolVersionsQuery(
    eventId ? { eventId } : skipToken,
  );
  const { data: titleEvent } = useGetSourcingEventQuery(eventId ? { eventId } : skipToken);
  useRecordTitle(titleEvent?.eventData?.title);

  const versions = versionsData?.versions ?? [];
  const sortedVersions = [...versions].sort((a, b) => b.versionNumber - a.versionNumber);
  const unchanged = draft?.differsFromLatestVersion === false;
  const canFinalise = draft?.canFinalise === true;
  const disabledReason = !canFinalise
    ? t("The protocol can be written and finalised once evaluation has started.")
    : unchanged
      ? t("Nothing has changed since the last version.")
      : "";

  const narrativesEditorId = `protocol-narratives:${eventId}`;
  const initialNarratives = useMemo(() => toNarrativesState(draft), [draft]);
  const saveNarratives = useCallback(
    async (fields: Partial<NarrativesState>): Promise<NarrativesState> => {
      if (!eventId) return toNarrativesState(undefined);
      let saved: ProtocolDocumentDto | undefined;
      for (const [editableKey, narrative] of Object.entries(fields.narratives ?? {})) {
        saved = await patch({ eventId, protocolDraftPatchDto: { editableKey, ...narrative } }).unwrap();
      }
      return toNarrativesState(saved);
    },
    [eventId, patch],
  );
  useCreateEditor<NarrativesState>(narrativesEditorId, {
    initialState: initialNarratives,
    save: saveNarratives,
    onError: apiError.trigger,
  });
  const { updateField: updateNarratives } = useEditor<NarrativesState>(narrativesEditorId);

  const onSaveNarrative = (editableKey: string, value: string, writtenAgainst?: NarrativeBasisDto | null) =>
    updateNarratives({ narratives: { [editableKey]: { value, writtenAgainst } } });

  const unwrittenFields = (draft?.sections ?? []).flatMap((section) =>
    section.blocks
      .filter((block) => block.editableKey && !block.value?.trim())
      .map((block) => block.label || section.heading),
  );

  const finaliseNow = async () => {
    if (!eventId) return;
    try {
      await finalise({ eventId }).unwrap();
      toast({ title: t("Protocol finalised"), status: "success", position: "top-right" });
      refetchVersions();
    } catch (e) {
      apiError.trigger(e);
    }
  };

  const onFinalise = () =>
    confirmationModal.show({
      title: String(t("Finalise the protocol?")),
      description: (
        <Stack spacing="3">
          <Text>
            {t("A finalised version is kept as it stands. You can still finalise a new version later.")}
          </Text>
          {unwrittenFields.length > 0 && (
            <Box>
              <Text fontWeight="semibold">
                {t("{{count}} field is still empty and will be blank in this version:", {
                  count: unwrittenFields.length,
                })}
              </Text>
              <UnorderedList mt="1">
                {unwrittenFields.map((field) => (
                  <ListItem key={field} fontSize="sm" color="smMuted">
                    {field}
                  </ListItem>
                ))}
              </UnorderedList>
            </Box>
          )}
        </Stack>
      ),
      confirmButtonText: String(t("Create PDF")),
      onConfirmed: finaliseNow,
    });

  return (
    <Box maxW="container.lg" mx="auto">
      {confirmationModal.modal}
      <Stack spacing="1" mb="6">
        <Link
          as="button"
          onClick={() => navigate(-1)}
          color="smPrimary"
          fontSize="sm"
          display="inline-flex"
          alignItems="center"
          gap="1"
          alignSelf="flex-start"
        >
          <Icon as={FiArrowLeft} /> {t("Back")}
        </Link>
        <Heading as="h1" size="lg" color="smText">
          {t("Procurement protocol")}
        </Heading>
      </Stack>

      {isLoading ? (
        <Center py="20">
          <Spinner color="smPrimary" />
        </Center>
      ) : isDraftError ? (
        <ListLoadError
          message={t("The protocol could not be loaded.")}
          onRetry={refetchDraft}
          isRetrying={isFetchingDraft}
        />
      ) : (
        <Stack spacing="5">
          {!canFinalise && (
            <Alert status="info" rounded="md" fontSize="sm">
              <AlertIcon />
              {t("The protocol can be written and finalised once evaluation has started.")}
            </Alert>
          )}
          {(draft?.sections ?? []).map((section) => (
            <ProtocolSectionView
              key={section.number}
              section={section}
              onSaveNarrative={onSaveNarrative}
              isReadOnly={!canFinalise}
            />
          ))}

          <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p={{ base: "4", md: "6" }}>
            <Flex justify="space-between" align="center" mb="3" gap="3">
              <Heading as="h2" size="sm" color="smText">
                {t("Finalised versions")}
              </Heading>
              <Tooltip label={disabledReason} isDisabled={!disabledReason}>
                <Box>
                  <Button
                    colorScheme="teal"
                    size="sm"
                    leftIcon={<Icon as={FiCheckCircle} />}
                    onClick={onFinalise}
                    isLoading={isFinalising}
                    isDisabled={!!disabledReason}
                  >
                    {t("Create PDF")}
                  </Button>
                </Box>
              </Tooltip>
            </Flex>
            <Divider mb="3" borderColor="smBorder" />
            {sortedVersions.length > 0 ? (
              <Stack spacing="2">
                {sortedVersions.map((version) => (
                  <HStack key={version.versionNumber} justify="space-between">
                    <Text fontSize="sm" color="smText">
                      {t("Version {{number}}", { number: version.versionNumber })}
                      <Text as="span" color="smMuted" ml="2">
                        {displayDateWithTime(version.finalisedAt, null, locale)}
                      </Text>
                    </Text>
                    <Button
                      size="sm"
                      variant="ghost"
                      colorScheme="teal"
                      leftIcon={<Icon as={FiDownload} />}
                      onClick={async () => {
                        try {
                          await downloadProtocolVersion({
                            downloadUrl: version.downloadUrl,
                            fileName: `${t("Procurement protocol")} v${version.versionNumber}`,
                          });
                        } catch (e) {
                          apiError.trigger(e);
                        }
                      }}
                    >
                      {t("Download")}
                    </Button>
                  </HStack>
                ))}
              </Stack>
            ) : (
              <Text fontSize="sm" color="smMuted">
                {t("No versions yet. Click Create PDF to create the first version.")}
              </Text>
            )}
          </Box>
        </Stack>
      )}
    </Box>
  );
};

interface Narrative {
  value: string;
  writtenAgainst?: NarrativeBasisDto | null;
}

interface NarrativesState extends Record<string, unknown> {
  narratives: Record<string, Narrative>;
}

const toNarrativesState = (document: ProtocolDocumentDto | undefined): NarrativesState => ({
  narratives: Object.fromEntries(
    (document?.sections ?? []).flatMap((section) =>
      section.blocks.flatMap((block) =>
        block.editableKey ? [[block.editableKey, { value: block.value ?? "", writtenAgainst: block.writtenAgainst }]] : [],
      ),
    ),
  ),
});
